import { Button, ButtonProps, IconButton, PlacementWithLogical } from '@chakra-ui/react'
import { ReactElement } from 'react'
import { Link } from 'react-router-dom'

import { Tooltip } from '../Tooltip'
import { ACTIONS, ActionKind } from './actions'

export interface IActionButtonProps extends Omit<ButtonProps, 'aria-label' | 'leftIcon' | 'children'> {
  action: ActionKind
  /** Sobrescreve o rótulo padrão da ação (tooltip + aria-label). */
  label?: string
  /**
   * Escape hatch para ações pontuais de domínio que não merecem entrada no registry
   * (ex.: "editar valor" com ícone de moeda). Mantém a geometria padrão do botão —
   * use com parcimônia; se o ícone se repetir em outra tela, vire uma ação no registry.
   */
  icon?: ReactElement
  tooltipPlacement?: PlacementWithLogical
  hideTooltip?: boolean
  /** Renderiza um Button com texto ao lado do ícone em vez de um IconButton. */
  showLabel?: boolean
  /** Navegação interna via react-router. */
  to?: string
  /** Link externo — abre em nova aba. */
  href?: string
}

/**
 * Botão de ação padronizado para linhas de tabela e ações inline.
 * Ícone, cor e rótulo vêm do registry em `actions.tsx` — não sobrescreva no call site
 * a não ser que a tela tenha um motivo real para divergir do padrão.
 */
export default function ActionButton({
  action,
  label,
  icon,
  tooltipPlacement,
  hideTooltip = false,
  showLabel = false,
  to,
  href,
  ...rest
}: IActionButtonProps) {
  const config = ACTIONS[action]
  const text = label ?? config.label
  const glyph = icon ?? config.icon

  const noUnderline = { textDecoration: 'none', _hover: { textDecoration: 'none' } }
  const linkProps = to
    ? { as: Link, to, ...noUnderline }
    : href
      ? { as: 'a', href, target: '_blank', rel: 'noopener noreferrer', ...noUnderline }
      : {}

  const sharedProps: ButtonProps = {
    colorScheme: config.colorScheme,
    variant: config.variant,
    size: 'sm',
    mr: 2,
    ...linkProps,
    ...rest
  }

  const button = showLabel ? (
    <Button leftIcon={glyph} {...sharedProps}>
      {text}
    </Button>
  ) : (
    <IconButton aria-label={text} icon={glyph} {...sharedProps} />
  )

  if (hideTooltip) return button

  return (
    <Tooltip label={text} placement={tooltipPlacement}>
      {/* Botão desabilitado não dispara eventos de mouse: o span garante que o tooltip apareça.
          `isLoading` também desabilita o elemento no Chakra, e é justamente quando o rótulo
          mais faz falta — o ícone vira spinner e perde a única pista do que o botão faz. */}
      {rest.isDisabled || rest.isLoading ? <span style={{ display: 'inline-block' }}>{button}</span> : button}
    </Tooltip>
  )
}
